import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ColorScheme = 'default' | 'blue' | 'green' | 'purple' | 'red'

interface ThemeState {
  mode: ThemeMode
  colorScheme: ColorScheme
  systemPreference: 'light' | 'dark'
  reducedMotion: boolean
  fontSize: 'sm' | 'base' | 'lg' | 'xl'
}

// Get system preference
const getSystemPreference = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Get stored theme or default to system
const getStoredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system'
  return (localStorage.getItem('theme') as ThemeMode) || 'system'
}

const getStoredColorScheme = (): ColorScheme => {
  if (typeof window === 'undefined') return 'default'
  return (localStorage.getItem('colorScheme') as ColorScheme) || 'default'
}

const getStoredFontSize = (): 'sm' | 'base' | 'lg' | 'xl' => {
  if (typeof window === 'undefined') return 'base'
  return (localStorage.getItem('fontSize') as any) || 'base'
}

const initialState: ThemeState = {
  mode: getStoredTheme(),
  colorScheme: getStoredColorScheme(),
  systemPreference: getSystemPreference(),
  reducedMotion: false,
  fontSize: getStoredFontSize(),
}

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload
      localStorage.setItem('theme', action.payload)
    },
    setColorScheme: (state, action: PayloadAction<ColorScheme>) => {
      state.colorScheme = action.payload
      localStorage.setItem('colorScheme', action.payload)
    },
    setSystemPreference: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.systemPreference = action.payload
    },
    setReducedMotion: (state, action: PayloadAction<boolean>) => {
      state.reducedMotion = action.payload
    },
    setFontSize: (state, action: PayloadAction<'sm' | 'base' | 'lg' | 'xl'>) => {
      state.fontSize = action.payload
      localStorage.setItem('fontSize', action.payload)
    },
    toggleTheme: (state) => {
      if (state.mode === 'system') {
        state.mode = state.systemPreference === 'dark' ? 'light' : 'dark'
      } else {
        state.mode = state.mode === 'dark' ? 'light' : 'dark'
      }
      localStorage.setItem('theme', state.mode)
    },
  },
})

export const {
  setThemeMode,
  setColorScheme,
  setSystemPreference,
  setReducedMotion,
  setFontSize,
  toggleTheme,
} = themeSlice.actions

export default themeSlice.reducer 